<template>
  <div>
    <Navbar />
    <div class="aspect-21/9 bg-cover flex items-center bg-center" :style="{ backgroundImage: 'url(/img/office.jpg)' }">
      <div class="standard-spacing w-full text-center">
        <h1 class="heading-1">Terms of Service</h1>
        <p class="subheading mt-4">Last updated 02 March 2021</p>
      </div>
    </div>
    <div class="py-16">
      <div class="standard-spacing space-y-12">
        <div class="space-y-4 mb-12">
          <p>
            The Hoap Corporate Platform has been developed by Lumin Sports Technology Pty Limited ABN 29 624 691 363 of
            Level 4, 27 Leigh Street, Adelaide SA 5000 ("<strong>we</strong>", "<strong>us</strong>",
            "<strong>our</strong>").
          </p>
          <p>
            The terms and conditions of this Agreement form a legally binding agreement between you and us in relation
            to your access to, and use of, the Hoap Corporate Platform.
          </p>
          <p>
            In accessing or using the Hoap Corporate Platform or the Services, you acknowledge that you have read,
            understood and agree to be bound by this Agreement. If you do not accept the terms of this Agreement, you
            must refrain from accessing or using the Hoap Corporate Platform.
          </p>
          <p>
            By clicking "I Agree" or by otherwise using the Hoap Corporate Platform (which includes this version and any
            other update, feature or future version of the Hoap Corporate Platform that we may provide) you agree to the
            terms of this Agreement, so you should read these terms carefully.
          </p>
          <p>
            Capitalised terms used but not otherwise defined in this Agreement have the meanings given to them in clause
            20.
          </p>
        </div>

        <div class="space-y-4 mb-12">
          <h3 id="access" class="text-2xl md:text-3xl leading-tight tracking-wide font-decorative">1. Access</h3>

          <p>
            The use of the Hoap Corporate Platform or the Services may require you to register with us or open an
            account. To do so, you must complete the registration process in the manner described on the Hoap Corporate
            Platform. By registering with us, you agree that all information you provide is accurate and up to date and
            does not infringe any person’s rights. You must also update your details if any of them change.
          </p>
          <p>
            Registration is non-transferable. You are responsible for all of the activities that occur under your
            account. You should carefully choose Your password and username and protect them from unauthorised use.
            Please let us know immediately of any unauthorised use of your account or any other breach of security. We
            are not liable for any loss if someone else uses your password or account.
          </p>
          <p>
            Access to the Hoap Corporate Platform is permitted on a temporary basis, and we reserve the right to
            suspend, withdraw, discontinue or amend all or any part of the Hoap Corporate Platform or the Services
            without notice. We will not be liable if, for any reason, the Hoap Corporate Platform is unavailable at any
            time or for any period. You are responsible for making all arrangements necessary for you and each
            Authorised User to have access to the Hoap Corporate Platform.
          </p>
        </div>

        <div class="space-y-4">
          <h3 id="license-terms" class="text-2xl md:text-3xl leading-tight tracking-wide font-decorative">
            2. License terms
          </h3>
          <p>
            Subject to this Agreement, we grant you a non-exclusive, world-wide, non-transferable, non-assignable
            licence to use the Hoap Corporate Platform. You may permit end users to use the Hoap Corporate Platformas
            part of your normal business operations ("<strong>Authorised Users</strong>")
          </p>
          <p>You must:</p>
          <ol class="ml-8 space-y-4 list-decimal">
            <li>supervise and control the use of the Hoap Corporate Platform in accordance with this Agreement; and</li>
            <li>
              ensure any Authorised Users (including your employees, sub-contractors and other agents who have
              authorised access to the Hoap Corporate Platform) are made aware of the terms of this Agreement.
            </li>
          </ol>

          <p>To the extent permitted by law and unless we otherwise consent in writing, You must not:</p>

          <ol class="ml-8 space-y-4 list-decimal">
            <li>
              use the Hoap Corporate Platform for any purpose or in any manner other than as set out in this Agreement;
            </li>
            <li>use the Hoap Corporate Platform in any way that could damage our reputation or goodwill;</li>
            <li>permit any third party to use the Hoap Corporate Platform other than as set out in this Agreement;</li>
            <li>
              copy, reproduce, make error corrections to or otherwise modify or adapt the Hoap Corporate Platform or
              create any derivative works based upon the Hoap Corporate Platform;
            </li>
            <li>
              de-compile, disassemble or otherwise reverse engineer the Hoap Corporate Platform or permit any third
              party to do so; or
            </li>
            <li>modify or remove any copyright or proprietary notices on the Hoap Corporate Platform.</li>
          </ol>
          <p>You agree that you will only use the Hoap Corporate Platform for lawful purposes.</p>
          <p>
            By accessing and using the Hoap Corporate Platform, you assume all risks associated with such access and
            use.
          </p>
        </div>

        <div class="space-y-4">
          <h3 id="fees" class="text-2xl md:text-3xl leading-tight tracking-wide font-decorative">3. Fees</h3>

          <p>
            Access to, and use of, the Hoap Corporate Platform or any part or feature of the Hoap Corporate Platform or
            the Services, may be subject to payment of certain Fees by you, as set out by us on the Hoap Corporate
            Platform. You acknowledge that the continued availability of any part or feature of the Hoap Corporate
            Platform or the Services may be conditional upon continued payment of such Fees.
          </p>
          <p>
            You acknowledge and agree we may change our Fees at any time. We will endeavour to notify you via the Hoap
            Corporate Platform of any changes to the Fees before they come into effect, however this may not always be
            possible.
          </p>
          <p>
            If you do not accept the Fees or any changes to the Fees, you must not continue to access or use the Hoap
            Corporate Platform.
          </p>
          <p>
            If you make any payments for Fees with a credit or charge card or an online payment account, you warrant
            that you are the cardholder or account holder and that the billing information provided is accurate, and you
            authorise us to charge the designated credit or charge card or online payment account for the total amount
            of the Fees, plus any applicable GST. All payments and purchases made in connection with the Hoap Corporate
            Platform are in Australian dollars.
          </p>
          <p>
            Where applicable, you agree to pay all GST and other similar taxes imposed in connection with the supply of
            the Services to you. You are responsible for all internet service provider fees, telecommunication and other
            charges required for your or any Authorised User’s connection to, or use of, the Hoap Corporate Platform
          </p>
          <p>
            We are not obliged to provide refunds of any Fees paid by you, however we may offer a refund where We
            determine that extenuating circumstances apply.
          </p>
        </div>

        <div class="space-y-4">
          <h3
            id="intellectual-property-rights"
            class="text-2xl md:text-3xl leading-tight tracking-wide font-decorative">
            4. Intellectual Property Rights
          </h3>
          <p>
            You acknowledge that, unless and to the extent stipulated to the contrary in this Agreement, we remain the
            sole owner of and retain all Intellectual Property Rights associated with the Hoap Corporate Platform
            (including any updates, enhancements, modifications, developments improvements or new features we may
            provide) and all pre-existing materials which are incorporated into the Hoap Corporate Platform, such as our
            specific processes, systems and methods.
          </p>
          <p>You must not create or permit to exist a Security Interest over the Hoap Corporate Platform.</p>
        </div>

        <div class="space-y-4">
          <h3 id="data" class="text-2xl md:text-3xl leading-tight tracking-wide font-decorative">5. Data</h3>
          <p>
            When you use the Hoap Corporate Platform, you or an Authorised User may send personal information to us. We
            will use, store and handle this information in accordance with the requirements of the Privacy Act 1988
            (Cth) that apply to us and our privacy policy which may be accessed at www.hoap.com. Our privacy policy
            applies to you and each Authorised User, and your respective use of the Hoap Corporate Platform at all
            times.
          </p>
          <p>
            You own and retain all Intellectual Property Rights in any data inputted or uploaded to the Hoap Corporate
            Platform by you or with your authority. You agree that we may de- identify and aggregate the results, data
            and other information generated, inputted or derived from Your or Authorised Users’ use of the Hoap
            Corporate Platform by removing any and all identifiers and removing or altering other information that may
            allow identification (<strong>De- identified Data</strong>). To the extent that De-identified Data is owned
            by you, you grant us an irrevocable, non-exclusive, perpetual, worldwide, royalty-free licence (with the
            ability to sub-licence) to use the De-identified Data for such purposes as we see fit.
          </p>
        </div>

        <div class="space-y-4">
          <h3 id="confidential-information" class="text-2xl md:text-3xl leading-tight tracking-wide font-decorative">
            6. Confidential Information
          </h3>
          <p>
            The parties must not disclose any Confidential Information provided by the other party (<strong
              >Disclosing Party</strong
            >) to any person, unless: (a) the Disclosing Party consents to the disclosure; (b) that information is
            De-Identified Data; (c) that information comes into the public domain; or (d) the party is required to
            disclose it by law.
          </p>
        </div>

        <div class="space-y-4">
          <h3 id="termination" class="text-2xl md:text-3xl leading-tight tracking-wide font-decorative">
            7. Termination
          </h3>
          <p>
            We may suspend your or any Authorised User’s access to the Hoap Corporate Platform (or any part of the Hoap
            Corporate Platform) or terminate this Agreement and your or any Authorised User’s access to the Hoap
            Corporate Platform if we reasonably consider that you or any Authorised User is in breach of any provision
            of this Agreement.
          </p>
          <p>
            We may, at our discretion, terminate this Agreement or your or any Authorised User’s access to all or part
            of the Hoap Corporate Platform without the need to provide reasons.
          </p>
          <p>If requested by us at any time, you must:</p>

          <ol class="ml-8 space-y-4 list-decimal">
            <li>upon expiry or termination of this Agreement; and</li>
            <li>at any time following expiry of termination of this Agreement,</li>
          </ol>
          <p>
            destroy or return any and all of our property in your or any Authorised User’s possession or control
            (including with respect to the Hoap Corporate Platform and associated documentation) as requested and in the
            manner directed by us.
          </p>
          <p>
            Clauses 4, 5, 6, 7, 8, 9, 10 and 16, and any other clause which under its terms or by implication ought to
            survive, will survive termination of this Agreement.
          </p>
        </div>

        <div class="space-y-4">
          <h3 id="disclaimer" class="text-2xl md:text-3xl leading-tight tracking-wide font-decorative">
            8. Disclaimer
          </h3>
          <p>
            The Hoap Corporate Platform is provided ‘as-is’. We provide no guarantees as to any outcomes from your use
            of the Hoap Corporate Platform. We do not promise that your use of the Hoap Corporate Platform will be
            uninterrupted or error or defect free or that errors or defects in the Hoap Corporate Platform will be
            corrected.
          </p>
        </div>

        <div class="space-y-4">
          <h3 id="limitation-of-liability" class="text-2xl md:text-3xl leading-tight tracking-wide font-decorative">
            9. Limitation of liability
          </h3>
          <p>
            To the maximum extent permitted by law, we (and our directors, employees and agents) do not accept any
            liability for, and you agree to release us from any claim in relation to any loss, howsoever caused,
            suffered or incurred by you arising from your or your Authorised Users’ use of the Hoap Corporate Platform
            (including without limitation any consequential losses or damages), except to the extent that we have
            contributed to that loss as a direct result of our breach or gross negligence, and any liability we owe is
            limited, to the maximum extent permitted under law, to the Fees you have paid to use the Hoap Corporate
            Platform, or (if we choose) us providing the service again.
          </p>
        </div>

        <div class="space-y-4">
          <h3 id="indemnity" class="text-2xl md:text-3xl leading-tight tracking-wide font-decorative">10. Indemnity</h3>
          <p>
            You agree to indemnify, defend and hold us (and our directors, employees and agents) harmless from and
            against all claims, liabilities, losses or expenses (including reasonable legal fees on a full indemnity
            basis) that arise from:
          </p>
          <ol class="ml-8 space-y-4 list-decimal">
            <li>any breach of this Agreement by you or a person authorised by you;</li>
            <li>any breach of any rights of a third party by you or a person authorised by you; or</li>
            <li>any breach of any applicable law by you or a person authorised by you,</li>
          </ol>
          <p>
            except to the extent that such claims, liabilities, losses or expenses are a direct result of our own breach
            or gross negligence.
          </p>
        </div>

        <div class="space-y-4">
          <h3 id="publicity-and-marketing" class="text-2xl md:text-3xl leading-tight tracking-wide font-decorative">
            11. Publicity and marketing
          </h3>
          <p>
            You agree that we may make or release promotional or marketing material (whether in online or printed media)
            that refers or relates to this Agreement, and that the Hoap Corporate Platform is used by you. In that
            regard, you grant us a non-exclusive right and limited licence to use your name and such of your Marks as
            reasonably necessary for such marketing or publicity purposes.
          </p>
        </div>

        <div class="space-y-4">
          <h3 id="severability" class="text-2xl md:text-3xl leading-tight tracking-wide font-decorative">
            12. Severability
          </h3>
          <p>
            If and to the extent any provision or part of a provision of this Agreement is void, voidable or
            unenforceable, such provision or part of a provision will be severed from this Agreement and will not affect
            the continued operation of the remaining terms of this Agreement.
          </p>
        </div>

        <div class="space-y-4">
          <h3 id="assignment" class="text-2xl md:text-3xl leading-tight tracking-wide font-decorative">
            13. Assignment
          </h3>
          <p>
            We may assign our rights or obligations under this Agreement at any time by notice in writing to you. You
            may only assign your rights or obligations under this Agreement with our prior written consent, which must
            not be unreasonably withheld.
          </p>
        </div>

        <div class="space-y-4">
          <h3 id="governing-law" class="text-2xl md:text-3xl leading-tight tracking-wide font-decorative">
            14. Governing Law
          </h3>
          <p>
            This Agreement shall be governed by the laws of South Australia and the parties irrevocably submit to the
            exclusive jurisdiction of the courts of South Australia.
          </p>
        </div>

        <div class="space-y-4">
          <h3 id="entire-agreement" class="text-2xl md:text-3xl leading-tight tracking-wide font-decorative">
            15. Entire agreement
          </h3>
          <p>
            This Agreement constitutes the entire agreement between the parties and may not be varied or waived except
            agreed to in writing by us.
          </p>
        </div>

        <div class="space-y-4">
          <h3 id="disputes" class="text-2xl md:text-3xl leading-tight tracking-wide font-decorative">16. Disputes</h3>
          <p>
            A party must not commence court proceedings or arbitration relating to any dispute arising from this
            Agreement without first complying with this clause, except where a party seeks urgent interlocutory relief
            or where the dispute relates to compliance with this clause. A party claiming that a dispute has arisen
            under this Agreement must give written notice of the details of the dispute to the other party. Each party
            that has given or received notice of the dispute under this clause must use its best endeavours to resolve
            the dispute. If within 20 business days of receipt of notice the parties to the dispute do not resolve the
            dispute, the parties must mediate the dispute in accordance with the mediation rules of the Resolution
            Institute of Australia and, if the parties cannot agree as to the person(s) to conduct the mediation, the
            parties must request the Resolution Institute of Australia to select the mediator and determine the
            mediator’s remuneration.
          </p>
        </div>

        <div class="space-y-4">
          <h3 id="time" class="text-2xl md:text-3xl leading-tight tracking-wide font-decorative">17. Time</h3>
          <p>
            In this Agreement: (a) time means South Australian time; (b) a business day means any day that is not a
            Saturday, Sunday or public holiday in South Australia; (c) If an act must be done on a day that is not a
            business day, it may be done on the next business day; (d) where time is to be calculated from a day or
            event, that day or the day of that event must be excluded from that calculation; and (e) unless stated
            otherwise, time is of the essence.
          </p>
        </div>

        <div class="space-y-4">
          <h3 id="waiver" class="text-2xl md:text-3xl leading-tight tracking-wide font-decorative">18. Waiver</h3>
          <p>
            Waiver of any power or right under this Agreement must be in writing signed by the party entitled to the
            benefit of that power or right and is effective only to the extent set out in that written waiver.
          </p>
        </div>

        <div class="space-y-4">
          <h3 id="relationship" class="text-2xl md:text-3xl leading-tight tracking-wide font-decorative">
            19. Relationship
          </h3>
          <p>
            You acknowledge that there is no relationship of principal and agent, employer and employee, partnership or
            joint venture between you and us.
          </p>
        </div>

        <div class="space-y-4">
          <h3 id="definitions" class="text-2xl md:text-3xl leading-tight tracking-wide font-decorative">
            20. Definitions
          </h3>
          <p>In this Agreement:</p>
          <p>
            <strong>“Agreement”</strong> means these terms and conditions for access to, and use of, the Hoap Coproate
            Platform;
          </p>
          <p><strong>“Authorised User”</strong> has the meaning given to that term in clause 1;</p>
          <p>
            <strong>“Confidential information”</strong> means any information that is by its nature confidential and
            includes (without limitation) information about the other party’s trade secrets, know-how, personnel,
            policies, business strategies, financial information and affairs, any particulars relating to the processes
            and methodology underlying its business, or anything a party identifies as ‘confidential’ or ‘proprietary’
            before it is disclosed;
          </p>
          <p><strong>“De-Identified Data”</strong> has the meaning given to that term in clause 5;</p>
          <p>
            <strong>“Fees”</strong> means any fee charged by us for the User’s use of the Hoap Corporate Platform or our
            Services;
          </p>
          <p>
            <strong>“Hoap Corporate Platform”</strong> means the software platform developed and provided by us known as
            the “Hoap”;
          </p>
          <p>
            <strong>“Intellectual Property Rights”</strong> includes any and all registered or unregistered rights in,
            including rights to apply registration, renewal or extension in, any copyrights, patents, inventions,
            discoveries, trade secrets, processes, methodologies, know-how, specifications, computer programs or
            software, source or object codes, trademarks, logos, designs or confidential information or any rights of a
            similar nature under the laws of Australia or anywhere else in the world;
          </p>
          <p>
            <strong>“Marks”</strong> means a party’s trade names, trademarks, service marks, logos, domain names, and
            other distinctive brand features;
          </p>
          <p>
            <strong>“Security Interest”</strong> means a security interest that is subject to the Personal Property
            Securities Act 2009 (Cth) or any other mortgage, pledge, lien, charge or other arrangement of any kind which
            in substance secures the payment of money or the performance of an obligation, or that gives a creditor
            priority over unsecured creditors;
          </p>
          <p>
            <strong>“Services”</strong> means the services, information, products and/or resources provided by us
            (including with respect to the Hoap Corporate Platform), or any other communication we may have with Users
            in accordance with this Agreement;
          </p>
          <p>
            <strong>“User”</strong> means any person who has access to, or otherwise uses, the Hoap Corporate Platform,
            and includes you and each Authorised User; and
          </p>
          <p><strong>“you”</strong> means the company or organisation who is a User.</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  setMetadata({ pageTitle: 'Terms of Service' })
</script>
